import { useState } from "react";
import { MOBILE_BREAKPOINT } from "../config";
import styled, { css, ThemeProp } from "../styles";
import { Card } from "./Card";
import { Text } from "./Text";

const Wrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 7px;
  &:hover {
    .popover {
      display: block;
    }
  }
`;

const Button = styled.button`
  border: 1px solid ${(p) => p.theme.color.card.divider};
  border-radius: ${(p) => p.theme.misc.borderRadiusSmall};
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 4px 10px;
  background: transparent;
  cursor: pointer;

  &:hover {
    background: ${(p) => p.theme.color.card.callout};
  }

  span {
    font-size: ${(p) => p.theme.typography.size.xxs};
    font-weight: ${(p) => p.theme.typography.weight.semi};
    font-family: ${(p) => p.theme.typography.bodyFamily};
    color: ${(p) => p.theme.color.typography.text};
    margin-bottom: -2px;
  }
`;

const IconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 13px;
`;

export const CopyTextButton = ({
  text,
  icon,
}: {
  text: string;
  icon: React.ReactNode;
}) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopyText = async (textToCopy: string) => {
    await navigator.clipboard.writeText(textToCopy);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 1500);
  };

  return (
    <Wrap>
      <Button onClick={() => handleCopyText(text)}>
        <IconWrap>{icon}</IconWrap>
        <span>{text}</span>
      </Button>
      <Tooltip className="popover">
        <StyledCard isCopied={isCopied}>
          <Text
            size="xxs"
            margin="s xxs"
            align="center"
            weight={isCopied ? "semi" : "normal"}
          >
            {isCopied ? "Copied!" : "Copy to clipboard"}
          </Text>
        </StyledCard>
      </Tooltip>
    </Wrap>
  );
};

const StyledCard = styled(Card)<{ isCopied: boolean }>`
  box-shadow: ${(p: ThemeProp) => p.theme.shadow.popover};
  background: ${(p: ThemeProp & { isCopied: boolean }) =>
    p.isCopied
      ? p.theme.color.constructive
      : p.theme.color.tooltip.background} !important;
  user-select: none;
  padding: 0px 10px;
  width: ${(p) => (p.isCopied ? "64px" : "120px")};
  box-sizing: border-box;
  border-radius: ${(p) => p.theme.misc.borderRadiusSmall};

  &:after {
    content: "";
    width: 12px;
    height: 12px;
    background: inherit;
    z-index: 1;
    top: 3px; // half height minus card margin top
    left: calc(50% - 6px);
    display: block;
    position: absolute;
    transform: rotateZ(45deg);
  }

  p {
    color: ${(p: ThemeProp) => p.theme.color.tooltip.text} !important;

    ${(p: ThemeProp & { isCopied: boolean }) =>
      p.isCopied &&
      css`
        color: #fff !important;
      `}
  }
`;

const Tooltip = styled.div<{
  width?: number;
}>`
  display: none;
  position: absolute;
  z-index: 999;
  padding: 8px 0;

  cursor: auto;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 2px 0;
  }
`;
